import { useState, useCallback } from 'react'
import { router } from '@inertiajs/react'
import { ValidationError } from '@/utils/validation'

interface ContactData {
  email: string
  phone: string
  isValidEmail: boolean
  isValidPhone: boolean
}

interface ContactPreferences {
  wantsEmailContact: boolean
  wantsPhoneContact: boolean
}

export const useContactForm = (answer_id?: string) => {
  const [contactPreferences, setContactPreferences] = useState<ContactPreferences>({
    wantsEmailContact: false,
    wantsPhoneContact: false
  })

  const [formData, setFormData] = useState<ContactData>({
    email: '',
    phone: '',
    isValidEmail: false,
    isValidPhone: false
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState<ValidationError>({
    email: '',
    phone: '',
    submit: ''
  })

  const handleEmailChange = useCallback(
    (value: string, isValid: boolean) => {
      setFormData((prev: ContactData) => ({ ...prev, email: value, isValidEmail: isValid }))
      if (contactPreferences.wantsEmailContact) {
        setErrors((prev: ValidationError) => ({
          ...prev,
          email: !value.trim() ? 'El email es requerido' : !isValid ? 'El email no es válido' : ''
        }))
      }
    },
    [contactPreferences.wantsEmailContact]
  )

  const handlePhoneChange = useCallback(
    (value: string, isValid: boolean) => {
      setFormData((prev: ContactData) => ({ ...prev, phone: value, isValidPhone: isValid }))
      if (contactPreferences.wantsPhoneContact) {
        setErrors((prev: ValidationError) => ({
          ...prev,
          phone: !value.trim() ? 'El teléfono es requerido' : !isValid ? 'El teléfono debe tener 9 dígitos' : ''
        }))
      }
    },
    [contactPreferences.wantsPhoneContact]
  )

  const isValidForm = useCallback(() => {
    if (!contactPreferences.wantsEmailContact && !contactPreferences.wantsPhoneContact) {
      return false
    }

    if (contactPreferences.wantsEmailContact && !formData.isValidEmail) {
      return false
    }

    if (contactPreferences.wantsPhoneContact && !formData.isValidPhone) {
      return false
    }

    return true
  }, [contactPreferences, formData.isValidEmail, formData.isValidPhone])

  const handleSubmit = useCallback(
    async (e: React.FormEvent, survey_set_hashid: string) => {
      e.preventDefault()
      if (!answer_id || isSubmitting) return

      if (!isValidForm()) {
        setErrors((prev: ValidationError) => ({
          ...prev,
          submit: 'Por favor, corrige los errores antes de enviar.'
        }))
        return
      }

      setIsSubmitting(true)
      setErrors({} as ValidationError)

      router.post(
        `/encuestas/${survey_set_hashid}/save_contact`,
        {
          email: contactPreferences.wantsEmailContact ? formData.email : null,
          phone: contactPreferences.wantsPhoneContact ? formData.phone : null,
          answer_id
        },
        {
          onFinish: () => setIsSubmitting(false),
          onError: (submitErrors: any) => {
            console.error('Error al enviar información de contacto:', submitErrors)
            setIsSubmitting(false)
            setErrors({ submit: 'Hubo un error al enviar la información. Por favor, intenta nuevamente.' })
          }
        }
      )
    },
    [answer_id, contactPreferences, formData, isSubmitting, isValidForm]
  )

  return {
    contactPreferences,
    setContactPreferences,
    formData,
    isSubmitting,
    errors,
    handleEmailChange,
    handlePhoneChange,
    handleSubmit,
    isValidForm
  }
}

export type UseContactFormReturn = ReturnType<typeof useContactForm>
